import { FC } from "react";
import { Controller } from "react-hook-form";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import SearchIcon from "@mui/icons-material/Search";
import Input from "../../components/Input";
import Checkbox from "../../components/Checkbox";
import { Button, ButtonType } from "../../components/Button";
import { t } from "i18next";
import { useLeagueFilterForm, LeagueFilterSharedProps } from "./useLeagueFilterForm";
import { StarIcon as StarSolidIcon } from "@heroicons/react/24/solid";

export type LeagueFilterFormMobileProps = LeagueFilterSharedProps & {
    onClose?: () => void;
};

const LeagueFilterFormMobile: FC<LeagueFilterFormMobileProps> = ({
    onClose,
    ...shared
}) => {
    const {
        control,
        handleSubmit,
        onSubmit,
        search,
        handleSearchChange,
        filteredOptions,
        isAllSelected,
        selectedLeaguesNo,
        length,
        handleToggleAll,
        handleToggleLeague,
    } = useLeagueFilterForm(shared);

    return (
        <form className="flex flex-col min-h-full" onSubmit={handleSubmit(onSubmit)}>
            <div className="flex py-4 items-center justify-center relative w-full">
                <button
                    className="absolute left-3 flex items-center gap-x-2"
                    type="button"
                    onClick={onClose}
                    aria-label="Back"
                >
                    <ArrowBackIosNewIcon className="align-text-bottom" style={{ fontSize: 18 }} />
                </button>
                <h1 className="font-bold text-2xl text-center">{t("League")}</h1>
            </div>
            <div className="p-3 border-gray-300 border-t border-x rounded-t-md">
                <Input
                    startAdornment={<SearchIcon className="absolute left-0 top-2" />}
                    className="pl-8 relative"
                    placeholder={t("Search") as string}
                    onChange={handleSearchChange}
                    value={search}
                />

                <div className="flex items-center justify-between pt-3">
                    <label className="flex items-center gap-x-2 cursor-pointer">
                        <Controller
                            name="isAllSelected"
                            control={control}
                            render={({ field }) => (
                                <Checkbox
                                    checked={field.value}
                                    onChange={(event) =>
                                        handleToggleAll(
                                            (event.target as HTMLInputElement).checked,
                                            field.onChange
                                        )
                                    }
                                />
                            )}
                        />
                        {t("Select / Cancel all")}
                    </label>

                    <span className="text-sm text-gray-700">
                        {selectedLeaguesNo}/{length} {t("selected")}
                    </span>
                </div>
            </div>
            <div className="flex-1 min-h-0">
                <Controller
                    name="selectedLeagues"
                    control={control}
                    render={({ field }) => (
                        <div className="h-full overflow-auto flex-wrap border-gray-300 border rounded-b-md">
                            {filteredOptions.length === 0 && (
                                <div className="p-8 text-center w-full text-gray-400 text-xl">
                                    {t("League is not found")}
                                </div>
                            )}
                            {filteredOptions.map((data) => {
                                const leagueId = data.value;
                                const isChecked = field.value.includes(leagueId);

                                return (
                                    <div key={data.value} className="p-3">
                                        <label className="cursor-pointer flex items-center gap-x-2">
                                            <Checkbox
                                                checked={isAllSelected ? !isChecked : isChecked}
                                                onChange={() =>
                                                    handleToggleLeague(
                                                        leagueId,
                                                        isChecked,
                                                        field.onChange,
                                                        field.value
                                                    )
                                                }
                                            />
                                            <span>{data.label}</span>
                                            {data.favorite && (
                                                <StarSolidIcon className="w-5 h-5 text-yellow-400" />
                                            )}
                                        </label>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                />
            </div>
            <div className="py-4 flex space-x-5 justify-center mt-auto border-t bg-white">
                <Button className="px-2" type="submit" buttonType={ButtonType.PRIMARY}>
                    {t("Confirm")}
                </Button>
                <Button type="button" className="px-2" onClick={onClose}>
                    {t("Cancel")}
                </Button>
            </div>
        </form>
    );
};

export default LeagueFilterFormMobile;
